import { useCallback } from 'react'

import { useListCardsApiV1CardsGet } from '#/shared/api/generated/billing/theCargoBilling'
import type { CreditCardResponse } from '#/shared/api/generated/billing/models'

import { CARD_BRAND, CARD_BRAND_ICON } from './saved-cards'
import type { CardBrand, SavedCard } from './saved-cards'

const PAGE_SIZE = 50

function mapBrand(value: string): CardBrand {
  if (value in CARD_BRAND_ICON) return value as CardBrand
  return CARD_BRAND.visa
}

function formatExpiry(month: number, year: number): string {
  const mm = String(month).padStart(2, '0')
  const yy = String(year).slice(-2)
  return `${mm}/${yy}`
}

function holderName(card: CreditCardResponse): string {
  const first = card.cardholder_first_name ?? ''
  const last = card.cardholder_last_name ?? ''
  const combined = `${first} ${last}`.trim()
  return combined || (card.nickname ?? '')
}

function toSavedCard(card: CreditCardResponse): SavedCard {
  return {
    id: card.id,
    brand: mapBrand(card.card_brand),
    last4: card.card_last4,
    holderName: holderName(card),
    expiry: formatExpiry(card.exp_month, card.exp_year),
    isDefault: card.is_default ?? false,
    badge: card.badge,
    updatedAt: card.updated_at,
  }
}

interface UseSettleCardsResult {
  cards: ReadonlyArray<SavedCard>
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useSettleCards(
  customerId: string | undefined,
  shipmentId: string | undefined,
  enabled: boolean,
): UseSettleCardsResult {
  const { data, isLoading, isError, refetch } = useListCardsApiV1CardsGet(
    { customer_id: customerId, shipment_id: shipmentId, size: PAGE_SIZE },
    {
      query: {
        enabled: enabled && Boolean(customerId),
        // Always show a fresh card list: refetch on every modal open
        // (re-enable) rather than serving the 5-minute cached result.
        staleTime: 0,
        refetchOnMount: 'always',
      },
    },
  )

  const cards = (data?.data.items ?? []).map(toSavedCard)
  const refetchCards = useCallback(() => {
    void refetch()
  }, [refetch])
  return { cards, isLoading, isError, refetch: refetchCards }
}
